import {
  renderSelectedOption,
  createPlaceholderHtml,
  createOptionsListHtml
} from './templates';
import { getSessionTokenData } from '../../../../utils/userUtils';
import { sentryLogger } from '../../../../monitor/monitor';

export const UI_ELEMENTS = {
  EMPTY_SELECTION: `<div class="empty-selection">
           <div class="plus-icon-container"></div>
         </div>`,
  INDEX_DESCRIPTION: 2
};

const initializeResponsibleCallbacks = () => {
  window.responsibleCallbacks = window.responsibleCallbacks || {};
};

const updateResponsibleUI = (options) => {
  const { taskId, columnName, responsables, column, optionsList } = options;
  const placeholderId = `responsible-placeholder-${columnName}-${taskId}`;
  const placeholderEl = document.getElementById(placeholderId);

  if (placeholderEl) {
    requestAnimationFrame(() => {
      placeholderEl.innerHTML =
        responsables && responsables.length > 0
          ? renderSelectedOption(responsables, column)
          : UI_ELEMENTS.EMPTY_SELECTION;
    });
  }

  const optionsListEl = document.querySelector(
    `[data-task-id="${taskId}"] .options-list`
  );

  if (optionsListEl && optionsList) {
    const newOptionsListHtml = createOptionsListHtml(
      optionsList,
      responsables,
      column
    );

    optionsListEl.innerHTML = '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newOptionsListHtml;
    while (tempDiv.firstChild) {
      optionsListEl.appendChild(tempDiv.firstChild);
    }
  }
};

const assignSubByResponsible = (task, subContracts) => {
  const sessionTokenData = getSessionTokenData();
  const availableRolesToUseThisFeature = [
    'superadmin',
    'admin',
    'projectleader',
    'planner',
    'superintendent'
  ];

  if (
    !sessionTokenData ||
    !availableRolesToUseThisFeature.includes(sessionTokenData.role)
  )
    return false;
  const subContractIds = new Set(subContracts?.map(({ id }) => id));

  const validResponsible = task.responsables?.find(({ subcontractId }) =>
    subContractIds.has(subcontractId)
  );

  if (validResponsible) {
    task.subcontractId = validResponsible.subcontractId;
    return true;
  }
  return false;
};

const updateResponsiblesList = (task, newResponsible) => {
  if (!Array.isArray(task.responsables)) {
    task.responsables = [];
  }

  const responsibleIndex = task.responsables.findIndex(
    (responsible) => String(responsible.id) === String(newResponsible.id)
  );

  if (responsibleIndex === -1) {
    task.responsables.push(newResponsible);
  } else {
    task.responsables.splice(responsibleIndex, 1);
  }
  return [...task.responsables];
};

const handleResponsibleSelection = async (task, newResponsible, options) => {
  const { column, gantt, callbackOnSelection, subContracts } = options;

  task.responsables = updateResponsiblesList(task, newResponsible);

  assignSubByResponsible(task, subContracts);

  updateResponsibleUI({
    taskId: task.id,
    columnName: column.name,
    responsables: task.responsables,
    column,
    optionsList: gantt.toSelectResponsables.filter(
      (option) => option.is_active !== false
    )
  });

  if (typeof callbackOnSelection === 'function') {
    callbackOnSelection(task, task.responsables);
  }
};

export const registerTaskCallbacks = (taskId, task, options) => {
  initializeResponsibleCallbacks();
  window.responsibleCallbacks = window.responsibleCallbacks || {};

  window.responsibleCallbacks[taskId] = {
    selection: (payload, dictionary) => {
      if (!payload || !payload.length || !dictionary) return;
      const newResponsibleId = payload[0];
      const newResponsible = dictionary[newResponsibleId];
      if (newResponsible && newResponsible.id) {
        handleResponsibleSelection(task, newResponsible, options);
      } else {
        sentryLogger.warn(
          'New responsible not found or invalid in dictionary for id:',
          newResponsibleId
        );
      }
    }
  };
};

export const prepareUIElements = (options) => {
  const { selectedObject, optionsList, column, taskId, isOnlyReadElement, t } =
    options;
  const selectedResponsibles =
    selectedObject[column.name] || selectedObject.responsables || [];
  const objectId = taskId || selectedObject.id || 'default';

  const placeholderHtml = createPlaceholderHtml(
    column.name,
    objectId,
    selectedResponsibles,
    column,
    isOnlyReadElement
  );
  const optionsListHtml = createOptionsListHtml(
    optionsList,
    selectedResponsibles,
    column,
    t
  );

  return { placeholderHtml, optionsListHtml };
};

const parseResponsibleValue = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    const parts = typeof value === 'string' ? value.split('-') : [];
    const valueId = parts[0] || null;
    const valueName = parts[1] || '';
    const valueLastname = parts[2] || '';

    if (!valueId) {
      sentryLogger.warn(
        'Failed to parse responsible ID from value:',
        value,
        error
      );
      return null;
    }
    return { id: valueId, name: valueName, lastname: valueLastname };
  }
};

const scheduleResponsibleDropdownHandler = (event) => {
  const target = event.target.closest('[data-action="responsibleSelection"]');
  if (!target) return;

  event.stopImmediatePropagation();

  const taskElement = target.closest('[data-task-id]');
  const taskId = taskElement?.dataset.taskId;
  if (!taskId) return;

  const callbackObject =
    window.responsibleCallbacks && window.responsibleCallbacks[taskId];
  if (callbackObject && typeof callbackObject.selection === 'function') {
    const valueString = target.dataset.value;
    const parsedValue = parseResponsibleValue(valueString);

    if (parsedValue && parsedValue.id) {
      callbackObject.selection([parsedValue.id], {
        [parsedValue.id]: parsedValue
      });
    } else {
      sentryLogger.warn(
        'Invalid value for responsible selection:',
        valueString,
        'Parsed:',
        parsedValue
      );
    }
  } else {
    sentryLogger.warn('No selection callback registered for task ID:', taskId);
  }
};

let globalResponsibleDropdownHandler = null;

export const setupGlobalHandlers = () => {
  if (!globalResponsibleDropdownHandler) {
    globalResponsibleDropdownHandler = scheduleResponsibleDropdownHandler;
    document.addEventListener('click', globalResponsibleDropdownHandler, true);
  }
};

export const cleanupResponsibleGlobalHandlers = () => {
  if (globalResponsibleDropdownHandler) {
    document.removeEventListener(
      'click',
      globalResponsibleDropdownHandler,
      true
    );
    globalResponsibleDropdownHandler = null;
  }
};

export const formatTagsToString = (tags, defaultValue = '') => {
  if (!tags || !tags.length) return defaultValue;
  return tags
    .map((tag) => `${tag.name || ''} ${tag.lastname || ''}`.trim())
    .join(', ');
};
